import {Detail, Icon} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {DISABLED_REASON_MESSAGE_ID, type ControlState} from './controlState';
import {formatCreditsReason} from './novaCreditsCopy';
import {RUN_CAP} from './novaInvocationStore';

export interface NovaPromoCardProps {
	// The same state the control beside it is in, so the two can never describe the task
	// differently - they are read from one source (see ConnectedNovaInvocationControl).
	state: ControlState;
	// The agent behind the current or last run, when naming it tells the reader something.
	specialistTitle?: string | null;
	creditsResetsOn?: string | null;
	// Whether the account as a whole is at its concurrent run limit. Distinct from this person's
	// own three-run ceiling: one is the account's capacity, the other is their own, and the copy
	// has to say which because only one of them clears by waiting for their own run to finish.
	accountAtLimit?: boolean;
}

// Sits beside the modal's Nova button and says what pressing it would do. A control that says only
// "Action" leaves the person to find out what Nova does by trying it on a task they care about;
// this is the one place in the modal with room to say it in a sentence.
const Card = styled.div`
	display: flex;
	align-items: center;
	gap: ${({theme}) => theme?.sys?.spacing?.['200'] ?? '8px'};
	/* No width of its own to claim: the box around the pair is drawn to their size, so the card
	   simply sits in it next to the control. The box states the inset and the gap between the two,
	   so neither is restated here as a margin. */
	min-width: 0;
`;

// The heading over its state line. Stacked rather than run together: the heading is the same in
// every state and the line under it is the part that changes, so a reader who has met the card
// before only has to read the second line.
const Lines = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

// Dimmed when Nova cannot run, so the card reads as unavailable at a glance rather than only on
// reading the reason. The icon alone carries it - the text stays at full contrast, because the
// reason is the thing that most needs reading in exactly that state.
const Mark = styled.span<{$dimmed: boolean}>`
	display: inline-flex;
	flex: none;
	opacity: ${({$dimmed}) => ($dimmed ? 0.4 : 1)};
`;

/**
 * Why this is not rendered for a person who cannot act: the card exists to get a run started, and
 * promoting a control they do not have is noise on a modal they opened to read. What they see
 * instead is defined by the view-only work, not here.
 */
export const NovaPromoCard = (props: NovaPromoCardProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {state} = props;

	// A refused control never gets an encouraging line beside it. Stating what Action would do, or
	// offering to continue, next to a button that cannot be pressed reads as the card not knowing
	// what the button is doing - so the reason replaces the message in every blocked state,
	// before a run and mid-conversation alike.
	const blockedReason = (): string => {
		if (state.kind !== 'disabled') return '';
		if (state.reason === 'out_of_credits') return formatCreditsReason(intl, props.creditsResetsOn);
		if (state.reason === 'run_cap_reached' && props.accountAtLimit) {
			return formatMessage({id: 'nova_invocation.control.reason_account_limit'});
		}
		// Every other reason is the control's own, word for word: the card sits right beside it.
		return formatMessage({id: DISABLED_REASON_MESSAGE_ID[state.reason]}, {count: RUN_CAP});
	};

	const message = (): string => {
		switch (state.kind) {
			case 'disabled':
				return blockedReason();
			case 'working':
				return props.specialistTitle
					? formatMessage({id: 'nova_invocation.promo.working_specialist'}, {agent: props.specialistTitle})
					: formatMessage({id: 'nova_invocation.control.status_working'});
			case 'ready_or_continue':
				return props.specialistTitle
					? formatMessage({id: 'nova_invocation.promo.continue_specialist'}, {agent: props.specialistTitle})
					: formatMessage({id: 'nova_invocation.promo.continue'});
			default:
				return formatMessage({id: 'nova_invocation.promo.action'});
		}
	};

	return (
		<Card data-cy="nova-promo-card">
			<Mark $dimmed={state.kind === 'disabled'} aria-hidden="true">
				<Icon icon="sparkle" size="s" />
			</Mark>
			<Lines>
				<Detail color="main" data-cy="nova-promo-card-heading">
					{formatMessage({id: 'nova_invocation.promo.heading'})}
				</Detail>
				<Detail color="medium" data-cy="nova-promo-card-message">
					{message()}
				</Detail>
			</Lines>
		</Card>
	);
};
